import { useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import { useServices } from '@/lib/ServicesContext';
import { ETYKIETA_ZAKRESU, tresciApi, type ZakresPaska, type ZastosowanieSkorki } from '@/lib/api/tresci';
import { oknoWarszawa, type PozycjaBiblioteki } from '@/lib/tresci/biblioteka';
import { etykietaOsoby, useOsobyPelne } from '@/lib/tresci/osobySkorek';
import { DOPISEK_WYNIK_NIEZNANY, komunikatBledu, wynikNieznany } from '@/lib/tresci/tresciUi';

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * PANEL ZASTOSOWAŃ SKÓRKI (OWNER 17.10) — „Aktywna: N zastosowań” jest klikalne
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Praktyczne zarządzanie, nie sucha historia: zakładki Wszyscy / Miasta / Szkoły / Aktywne osoby, w każdej aktywne
 * i zaplanowane zastosowania z pełnym adresatem (osoba = imię i nazwisko + pełny e-mail), terminem, priorytetem,
 * stanem; „Wyłącz” (bez usuwania — historia zostaje) i „+ Dodaj filtr” (to samo okno „Zaplanuj skórkę”, z zakresem
 * zakładki). Zmiana istniejącego zastosowania = wyłącz + dodaj nowe (zastosowanie nie jest edytowane w miejscu,
 * żeby historia pokazywała, kto i kiedy co zmienił).
 */

const ZAKLADKI: { zakres: ZakresPaska; etykieta: string; pusto: string }[] = [
  { zakres: 'GLOBAL', etykieta: 'Wszyscy', pusto: 'Brak zastosowań dla wszystkich.' },
  { zakres: 'CITY', etykieta: 'Miasta', pusto: 'Brak aktywnych zastosowań dla miast.' },
  { zakres: 'SCHOOL', etykieta: 'Szkoły', pusto: 'Brak aktywnych zastosowań dla szkół.' },
  { zakres: 'USER', etykieta: 'Aktywne osoby', pusto: 'Brak aktywnych zastosowań dla konkretnych osób.' },
];

const STAN: Record<string, { tekst: string; kolor: 'success' | 'info' }> = {
  ACTIVE: { tekst: 'Aktywne', kolor: 'success' },
  SCHEDULED: { tekst: 'Zaplanowane', kolor: 'info' },
};

export interface PanelZastosowanProps {
  p: PozycjaBiblioteki;
  zastosowania: ZastosowanieSkorki[];
  mozePublikowac: boolean;
  zapisDostepny: boolean;
  onZamknij: () => void;
  onDodaj: (zakres: ZakresPaska) => void;
  onHistoria: (z: ZastosowanieSkorki) => void;
  onZmiana: (tekst: string) => void;
  onWynikNieznany: () => void;
}

export function PanelZastosowan({ p, zastosowania, mozePublikowac, zapisDostepny, onZamknij, onDodaj, onHistoria, onZmiana, onWynikNieznany }: PanelZastosowanProps) {
  const { api } = useServices();
  const biezace = useMemo(() => zastosowania.filter((z) => z.themeId === p.themeId && (z.effectiveStatus === 'ACTIVE' || z.effectiveStatus === 'SCHEDULED')), [zastosowania, p.themeId]);
  const [zakladka, setZakladka] = useState<ZakresPaska>(() => (ZAKLADKI.find((t) => biezace.some((z) => z.scope === t.zakres))?.zakres ?? 'GLOBAL'));
  const [blad, setBlad] = useState<string | null>(null);
  const [trwa, setTrwa] = useState<number | null>(null);
  const osoby = useOsobyPelne(biezace.filter((z) => z.scope === 'USER').flatMap((z) => z.targets.map((t) => t.id)));

  const wylacz = async (z: ZastosowanieSkorki) => {
    setTrwa(z.id);
    setBlad(null);
    try {
      await tresciApi.wylaczZastosowanie(api, z.id);
      onZmiana(`Wyłączono zastosowanie: ${p.nazwa} · ${ETYKIETA_ZAKRESU[z.scope]} · ${oknoWarszawa(z.startsAt, z.endsAt)}.`);
    } catch (e) {
      setBlad(komunikatBledu(e) + (wynikNieznany(e) ? DOPISEK_WYNIK_NIEZNANY : ''));
      if (wynikNieznany(e)) onWynikNieznany();
    } finally {
      setTrwa(null);
    }
  };

  const widoczne = biezace.filter((z) => z.scope === zakladka);
  const opis = ZAKLADKI.find((t) => t.zakres === zakladka)!;

  return (
    <Dialog open onClose={onZamknij} maxWidth="md" fullWidth aria-labelledby="panel-zastosowan-tytul">
      <DialogTitle id="panel-zastosowan-tytul">Zastosowania skórki: {p.nazwa}</DialogTitle>
      <DialogContent>
        <Tabs value={zakladka} onChange={(_, v: ZakresPaska) => setZakladka(v)} aria-label="Zakres zastosowań" variant="scrollable" sx={{ mb: 2 }}>
          {ZAKLADKI.map((t) => (
            <Tab key={t.zakres} value={t.zakres} label={`${t.etykieta} (${biezace.filter((z) => z.scope === t.zakres).length})`} />
          ))}
        </Tabs>
        {blad ? <Alert severity="error" role="alert" sx={{ mb: 1 }}>{blad}</Alert> : null}
        <Stack spacing={1.5} data-testid={`zastosowania-${zakladka}`}>
          {widoczne.length === 0 ? <Typography variant="body2" sx={{ color: 'text.secondary' }}>{opis.pusto}</Typography> : null}
          {widoczne.map((z) => (
            <Box key={z.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5 }} data-testid={`panel-zastosowanie-${z.id}`}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'space-between', alignItems: { sm: 'flex-start' } }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }}>
                    <Chip size="small" color={STAN[z.effectiveStatus]?.kolor ?? 'default'} label={STAN[z.effectiveStatus]?.tekst ?? z.effectiveStatus} />
                    <Typography variant="body2"><b>{oknoWarszawa(z.startsAt, z.endsAt)}</b> · priorytet {z.priority}</Typography>
                  </Stack>
                  {z.scope === 'GLOBAL' ? <Typography variant="body2">Wszyscy użytkownicy</Typography> : (
                    <Stack component="ul" sx={{ m: 0, pl: 2.5 }} spacing={0.25}>
                      {z.targets.map((t) => (
                        <Typography component="li" variant="body2" key={t.id} sx={{ wordBreak: 'break-word' }}>
                          {z.scope === 'USER' ? etykietaOsoby(t.id, t.name, osoby) : t.name}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                  {typeof z.potentialRecipients === 'number' ? (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{z.potentialRecipients.toLocaleString('pl-PL')} potencjalnych odbiorców</Typography>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {mozePublikowac ? (
                    <Button size="small" color="warning" disabled={!zapisDostepny || trwa === z.id} onClick={() => { void wylacz(z); }}>
                      {trwa === z.id ? 'Wyłączam…' : 'Wyłącz'}
                    </Button>
                  ) : null}
                  <Button size="small" onClick={() => onHistoria(z)}>Historia</Button>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
          Zmiana terminu lub adresatów = „Wyłącz” + „Dodaj filtr” (historia pokazuje obie zmiany).
        </Typography>
      </DialogContent>
      <DialogActions>
        {mozePublikowac ? (
          <Button variant="contained" disabled={!zapisDostepny || !p.doZastosowania} onClick={() => onDodaj(zakladka)}>
            + Dodaj filtr ({ETYKIETA_ZAKRESU[zakladka]})
          </Button>
        ) : null}
        <Button onClick={onZamknij}>Zamknij</Button>
      </DialogActions>
    </Dialog>
  );
}
